import { Link, useParams } from "react-router-dom"
import SEO from './SEO';

const NewsPost = () => {
  const { slug } = useParams()

  // Mock data for demonstration
  const post = {
    title: "New Advanced Cardiac Care Unit Now Open",
    content: `
      <p>We are excited to announce the opening of our new state-of-the-art Cardiac Care Unit at MAMS Hospitals. This advanced facility represents our commitment to providing the highest quality of cardiac care to our patients.</p>
      
      <h2>Cutting-Edge Technology</h2>
      <p>The new Cardiac Care Unit is equipped with the latest monitoring and treatment technologies, including:</p>
      <ul>
        <li>Advanced cardiac monitoring systems</li>
        <li>State-of-the-art diagnostic equipment</li>
        <li>Modern cardiac catheterization laboratory</li>
        <li>Specialized cardiac intensive care beds</li>
      </ul>
      
      <h2>Expert Care Team</h2>
      <p>Our dedicated team of cardiologists, cardiac surgeons, specialized nurses, and support staff work together to provide comprehensive care for patients with various heart conditions. The team is led by Dr. Malayadri, our Chief of Cardiology, who brings over 20 years of experience in treating complex cardiac cases.</p>
      
      <h2>Comprehensive Services</h2>
      <p>The new unit offers a wide range of services, including:</p>
      <ul>
        <li>Non-invasive cardiac testing</li>
        <li>Interventional cardiology procedures</li>
        <li>Cardiac rehabilitation programs</li>
        <li>Preventive cardiology services</li>
        <li>Heart failure management</li>
      </ul>
      
      <p>This expansion allows us to better serve our community and provide timely, life-saving care to patients with heart conditions. The unit is designed with patient comfort in mind, featuring private rooms, family waiting areas, and a healing environment that promotes recovery.</p>
      
      <h2>Appointment Information</h2>
      <p>To schedule an appointment with our cardiology department or to learn more about our cardiac services, please contact us at +91-7399999981 or book an appointment online.</p>
      
      <p>We are committed to heart health and look forward to serving you with excellence and compassion at our new Cardiac Care Unit.</p>
    `,
    date: "May 2, 2025",
    author: "Dr. Malayadri",
    category: "Hospital News",
    image: "/placeholder.svg?height=400&width=800",
    tags: ["Cardiology", "Healthcare", "Medical Facilities", "Patient Care"],
  }

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Hospital News & Health Updates | MAMS Hospitals Hyderabad"
        description="Stay updated with the latest news, health tips, and medical updates from MAMS Hospitals Bachupally, Hyderabad."
        keywords="MAMS Hospitals news, hospital updates, health news Hyderabad, Bachupally hospital news"
        canonicalUrl="https://mamshospitals.com/news"
      />
      <section className="bg-primary/5 py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link to="/news" className="inline-flex items-center text-teal-600 font-medium mb-6">
            <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to News
          </Link>
          <h1 className="text-3xl md:text-4xl font-semibold text-gray-800 mb-4">{post.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-gray-500 text-sm">
            <div className="flex items-center gap-1">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span>{post.date}</span>
            </div>
            <div className="flex items-center gap-1">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              <span>{post.author}</span>
            </div>
            <div className="flex items-center gap-1">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                />
              </svg>
              <span>{post.category}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <div className="w-full h-[400px] relative">
        <img src={post.image || "/placeholder.svg"} alt={post.title} className="w-full h-full object-cover" />
      </div>

      {/* News Content */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="prose prose-lg max-w-none">
            <div className="text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: post.content }} />
          </div>

          {/* Tags */}
          <div className="mt-12 pt-6 border-t border-gray-200">
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, index) => (
                <Link
                  key={index}
                  to={`/news/tag/${tag.toLowerCase().replace(/\s+/g, "-")}`}
                  className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm hover:bg-gray-200 transition-colors"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </div>

          {/* Share and Navigation */}
          <div className="mt-12 flex justify-between">
            <Link to="/news" className="inline-flex items-center text-teal-600 font-medium">
              <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to News
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

export default NewsPost
